import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError, eur } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLUSTERS } from "@/lib/constants";
import { SinistriDeclaration, SINISTRI_YEARS } from "@/components/SinistriDeclaration";
import { IddDeclaration } from "@/components/IddDeclaration";
import { ArrowRight, Mountain, Info, Download, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { HERO, ObbligoCheck, CatNatInfoSections } from "@/components/CatNatInfo";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyConsent, LegalFooterLinks } from "@/components/LegalConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VOLANTINO_URL = `${BACKEND_URL}/api/public/catnat/volantino`;
const TIPOLOGIE = ["Cooperativa (generico)", ...CLUSTERS];
const UW_BOOLS = [
  ["sostituzione_altra_compagnia", "Sostituzione di polizza in essere con altra compagnia"],
  ["sinistri_pregressi", "Sinistri catastrofali pregressi dichiarati"],
];

export default function CatNatPublic() {
  const formRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [contatti, setContatti] = useState({ ragione_sociale: "", cluster: "", referente: "", email: "", telefono: "", note: "" });
  const [indirizzo, setIndirizzo] = useState("");
  const [comune, setComune] = useState("");
  const [provincia, setProvincia] = useState("");
  const [cap, setCap] = useState("");
  const [zona, setZona] = useState("");
  const [classeAlluvione, setClasseAlluvione] = useState("media");
  const [materiale, setMateriale] = useState("Muratura");
  const [anno, setAnno] = useState("Sconosciuto");
  const [piani, setPiani] = useState("Sconosciuto");
  const [pianoRischio, setPianoRischio] = useState("Piano terra");
  const [interrati, setInterrati] = useState(false);
  const [attivita, setAttivita] = useState("");
  const [partite, setPartite] = useState({ fabbricato: "", macchinario: "", terreni: "" });
  const [eventi, setEventi] = useState({ terremoto: true, inondazione: true, franamento: true });
  const [scoperto, setScoperto] = useState(15);
  const [limite, setLimite] = useState(100);
  const [answers, setAnswers] = useState({});
  const [sinistri, setSinistri] = useState(null);
  const [idd, setIdd] = useState(null);
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);

  useEffect(() => { api.get("/public/catnat/config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  useEffect(() => {
    const prov = provincia.trim().toUpperCase();
    if (prov.length < 2) return;
    api.get(`/public/catnat/zona?provincia=${prov}`).then(({ data }) => {
      if (data.zona_sismica) setZona(data.zona_sismica);
    }).catch(() => {});
  }, [provincia]);

  const totaleSomme = useMemo(
    () => Object.values(partite).reduce((s, v) => s + Number(v || 0), 0), [partite]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!indirizzo.trim() || !comune.trim() || !provincia.trim() || !cap.trim()) {
      setError("Indirizzo completo obbligatorio (via e civico, comune, provincia, CAP) per la geolocalizzazione del rischio.");
      return;
    }
    if (!privacyOk) { setError("Per procedere devi accettare l'informativa privacy."); return; }
    setLoading(true);
    try {
      const payload = {
        ragione_sociale: contatti.ragione_sociale, cluster: contatti.cluster,
        referente: contatti.referente, email: contatti.email, telefono: contatti.telefono, note: contatti.note,
        quotazione: {
          indirizzo: indirizzo.trim(), comune, provincia: provincia.trim().toUpperCase(), cap,
          zona_sismica: zona || null, classe_alluvione: classeAlluvione, materiale, anno_costruzione: anno,
          numero_piani: piani, piano_rischio: pianoRischio, interrati, attivita,
          partite: Object.fromEntries(Object.entries(partite).map(([k, v]) => [k, Number(v || 0)])),
          eventi, scoperto: Number(scoperto), limite_indennizzo: Number(limite),
          data_effetto: dataEffetto, underwriting_answers: answers,
        },
        sinistri, idd,
      };
      await api.post("/public/catnat/request", payload);
      setSent(true);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-30 border-b border-border bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <Brand />
          <a href={VOLANTINO_URL} target="_blank" rel="noopener noreferrer" data-testid="catnat-public-volantino"
            className="hidden items-center gap-2 text-sm font-medium text-forest/70 hover:text-forest sm:inline-flex">
            <Download className="h-4 w-4" /> Volantino (PDF)
          </a>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-6 px-4 py-6 md:px-6 md:py-8" data-testid="catnat-public">
        {/* HERO */}
        <div className="overflow-hidden rounded-2xl border border-forest/15 bg-forest p-8 text-white md:p-10">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
            <Info className="h-3.5 w-3.5" /> {HERO.badge}
          </div>
          <div className="flex items-start gap-4">
            <span className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 sm:flex">
              <Mountain className="h-7 w-7" strokeWidth={1.75} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-semibold leading-tight md:text-4xl">{HERO.title}</h1>
              <p className="mt-3 max-w-2xl text-sm text-white/85 md:text-base">{HERO.subtitle}</p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Button size="lg" onClick={scrollToForm} data-testid="catnat-public-cta-hero"
                  className="gap-2 bg-white text-forest hover:bg-white/90">
                  Richiedi la proposta <ArrowRight className="h-4 w-4" />
                </Button>
                <a href={VOLANTINO_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-white/85 underline-offset-4 hover:text-white hover:underline">
                  <Download className="h-4 w-4" /> Scarica il volantino informativo (PDF)
                </a>
              </div>
            </div>
          </div>
        </div>

        <ObbligoCheck
          renderCta={() => (
            <Button onClick={scrollToForm} data-testid="check-cta" className="gap-2 bg-forest text-white hover:bg-forest/90">
              Richiedi la proposta <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        />

        <CatNatInfoSections />

        {/* Form pubblico di richiesta proposta */}
        <div ref={formRef} data-testid="catnat-public-form-section" className="scroll-mt-20">
          {sent ? (
            <div data-testid="catnat-public-success" className="rounded-2xl border border-forest/20 bg-white p-8 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
              <h2 className="mt-4 font-heading text-2xl font-semibold text-forest">Richiesta inviata!</h2>
              <p className="mt-2 text-muted-foreground">Grazie. La tua richiesta di proposta CAT NAT è stata inoltrata al nostro team, che ti ricontatterà a breve.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-forest">Richiesta di proposta CAT NAT</h2>
                <p className="mt-1 text-sm text-muted-foreground">Compila i dati dell'impresa e del rischio: riceverai una proposta su misura.</p>
              </div>

              {/* Contatti */}
              <Card className="rounded-xl border-border/60 p-5">
                <h3 className="mb-4 font-heading text-lg font-semibold text-forest">Dati dell'impresa / ente</h3>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Nome dell'impresa / ente <span className="text-terracotta">*</span></Label>
                    <Input data-testid="catnat-public-ragione-sociale" required value={contatti.ragione_sociale}
                      onChange={(e) => setContatti({ ...contatti, ragione_sociale: e.target.value })} className="h-12" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tipologia</Label>
                    <Select value={contatti.cluster} onValueChange={(v) => setContatti({ ...contatti, cluster: v })}>
                      <SelectTrigger data-testid="catnat-public-cluster" className="h-12"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                      <SelectContent>{TIPOLOGIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5"><Label>Referente</Label>
                      <Input data-testid="catnat-public-referente" value={contatti.referente}
                        onChange={(e) => setContatti({ ...contatti, referente: e.target.value })} className="h-12" /></div>
                    <div className="space-y-1.5"><Label>Telefono</Label>
                      <Input data-testid="catnat-public-telefono" value={contatti.telefono}
                        onChange={(e) => setContatti({ ...contatti, telefono: e.target.value })} className="h-12" /></div>
                    <div className="space-y-1.5"><Label>Email</Label>
                      <Input data-testid="catnat-public-email" type="email" value={contatti.email}
                        onChange={(e) => setContatti({ ...contatti, email: e.target.value })} className="h-12" /></div>
                  </div>
                </div>
              </Card>

              {/* Ubicazione del rischio */}
              <Card className="rounded-xl border-border/60 p-5">
                <h3 className="mb-1 font-heading text-lg font-semibold text-forest">Ubicazione del rischio</h3>
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>L'indirizzo esatto <b>con numero civico e CAP</b> è obbligatorio: serve per la geolocalizzazione del rischio in fase di emissione.</span>
                </div>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Indirizzo (via e numero civico) <span className="text-terracotta">*</span></Label>
                    <Input data-testid="catnat-public-indirizzo" required value={indirizzo}
                      onChange={(e) => setIndirizzo(e.target.value)} placeholder="es. Via Gramsci 8/G" className="h-12" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5"><Label>Comune <span className="text-terracotta">*</span></Label>
                      <Input data-testid="catnat-public-comune" required value={comune} onChange={(e) => setComune(e.target.value)} placeholder="es. Gesturi" className="h-12" /></div>
                    <div className="space-y-1.5"><Label>Provincia <span className="text-terracotta">*</span></Label>
                      <Input data-testid="catnat-public-provincia" required value={provincia} maxLength={2}
                        onChange={(e) => setProvincia(e.target.value.toUpperCase())} placeholder="es. SU" className="h-12" /></div>
                    <div className="space-y-1.5"><Label>CAP <span className="text-terracotta">*</span></Label>
                      <Input data-testid="catnat-public-cap" required value={cap} onChange={(e) => setCap(e.target.value)} placeholder="es. 09020" className="h-12" /></div>
                  </div>
                </div>
              </Card>

              {/* Caratteristiche del fabbricato */}
              {config && (
                <Card className="rounded-xl border-border/60 p-5">
                  <h3 className="mb-4 font-heading text-lg font-semibold text-forest">Caratteristiche del fabbricato</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label>Materiale strutture</Label>
                      <Select value={materiale} onValueChange={setMateriale}>
                        <SelectTrigger data-testid="catnat-public-materiale" className="h-12"><SelectValue /></SelectTrigger>
                        <SelectContent>{config.materiali.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1.5"><Label>Anno di costruzione</Label>
                      <Select value={anno} onValueChange={setAnno}>
                        <SelectTrigger data-testid="catnat-public-anno" className="h-12"><SelectValue /></SelectTrigger>
                        <SelectContent>{config.anni_costruzione.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1.5"><Label>Numero piani</Label>
                      <Select value={piani} onValueChange={setPiani}>
                        <SelectTrigger data-testid="catnat-public-piani" className="h-12"><SelectValue /></SelectTrigger>
                        <SelectContent>{config.numero_piani.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1.5"><Label>Piano del rischio</Label>
                      <Select value={pianoRischio} onValueChange={setPianoRischio}>
                        <SelectTrigger data-testid="catnat-public-piano-rischio" className="h-12"><SelectValue /></SelectTrigger>
                        <SelectContent>{config.piano_rischio.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="sm:col-span-2 flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
                      <Label className="cursor-pointer">Presenza di piani interrati</Label>
                      <Switch data-testid="catnat-public-interrati" checked={interrati} onCheckedChange={setInterrati} />
                    </div>
                    <div className="sm:col-span-2 space-y-1.5"><Label>Attività esercitata</Label>
                      <Input data-testid="catnat-public-attivita" value={attivita} onChange={(e) => setAttivita(e.target.value)}
                        placeholder="es. Cooperativa di servizi alle persone" className="h-12" /></div>
                  </div>
                </Card>
              )}

              {/* Partite assicurate */}
              {config && (
                <Card className="rounded-xl border-border/60 p-5">
                  <h3 className="mb-1 font-heading text-lg font-semibold text-forest">Partite assicurate</h3>
                  <p className="mb-4 text-xs text-muted-foreground">Indica la somma assicurata per ciascuna partita (0 = non assicurata). Totale: <b>{eur(totaleSomme)}</b> / max {eur(config.limite_sezione)}.</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {config.partite.map((p) => (
                      <div key={p.code} className="space-y-1.5">
                        <Label>{p.label} <span className="text-[11px] text-muted-foreground">(≤ {eur(p.sottolimite)})</span></Label>
                        <Input data-testid={`catnat-public-partita-${p.code}`} type="number" min={0} value={partite[p.code]}
                          onChange={(e) => setPartite({ ...partite, [p.code]: e.target.value })} placeholder="€ 0" className="h-12" />
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Eventi catastrofali e scoperto */}
              {config && (
                <Card className="rounded-xl border-border/60 p-5">
                  <h3 className="mb-4 font-heading text-lg font-semibold text-forest">Eventi catastrofali e scoperto</h3>
                  <div className="space-y-3">
                    {config.eventi.map((e) => (
                      <label key={e.code} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm">
                        <Checkbox data-testid={`catnat-public-evento-${e.code}`} checked={!!eventi[e.code]}
                          onCheckedChange={(v) => setEventi({ ...eventi, [e.code]: !!v })} />
                        <span className="text-forest">{e.label}</span>
                      </label>
                    ))}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5"><Label>Scoperto</Label>
                        <Select value={String(scoperto)} onValueChange={(v) => setScoperto(Number(v))}>
                          <SelectTrigger data-testid="catnat-public-scoperto" className="h-12"><SelectValue /></SelectTrigger>
                          <SelectContent>{config.scoperti.map((s) => <SelectItem key={s} value={String(s)}>{s}%</SelectItem>)}</SelectContent>
                        </Select></div>
                      <div className="space-y-1.5"><Label>Limite di indennizzo</Label>
                        <Select value={String(limite)} onValueChange={(v) => setLimite(Number(v))}>
                          <SelectTrigger data-testid="catnat-public-limite" className="h-12"><SelectValue /></SelectTrigger>
                          <SelectContent>{config.limiti_indennizzo.map((l) => <SelectItem key={l} value={String(l)}>{l}%</SelectItem>)}</SelectContent>
                        </Select></div>
                    </div>
                    <div className="space-y-1.5"><Label>Data di effetto desiderata</Label>
                      <Input data-testid="catnat-public-data-effetto" type="date" value={dataEffetto}
                        onChange={(e) => setDataEffetto(e.target.value)} className="h-12" /></div>
                  </div>
                </Card>
              )}

              {/* Questionario + Sinistri + IDD */}
              <Card className="rounded-xl border-border/60 p-5">
                <h3 className="mb-4 font-heading text-lg font-semibold text-forest">Questionario assuntivo</h3>
                <div className="space-y-2.5">
                  {UW_BOOLS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm">
                      <Checkbox data-testid={`catnat-public-uw-${key}`} checked={!!answers[key]}
                        onCheckedChange={(v) => setAnswers({ ...answers, [key]: !!v })} />
                      <span className="text-forest">{label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-4 border-t border-border/60 pt-4">
                  <SinistriDeclaration value={sinistri} onChange={setSinistri}
                    periodoAnni={SINISTRI_YEARS.cat_nat_impresa} testidPrefix="catnat-public-sinistri" />
                </div>
                <div className="mt-4 border-t border-border/60 pt-4">
                  <p className="mb-2 text-sm font-semibold text-forest">Valutazione di adeguatezza (IDD)</p>
                  <IddDeclaration value={idd} onChange={setIdd} testidPrefix="catnat-public-idd" businessOnly hideAdeguatezza />
                </div>
              </Card>

              {error && <p className="text-sm text-red-600" data-testid="catnat-public-error">{error}</p>}
              <PrivacyConsent checked={privacyOk} onCheckedChange={setPrivacyOk} testid="catnat-public-privacy-consent" />
              <Button type="submit" data-testid="catnat-public-submit" disabled={loading || !privacyOk}
                className="h-12 w-full rounded-xl bg-terracotta text-base hover:bg-terracotta-hover">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia richiesta di proposta"}
              </Button>
            </form>
          )}
        </div>

        <p className="pb-6 text-center text-xs text-muted-foreground">
          Contenuto informativo, non costituisce proposta contrattuale · verificare sempre le Condizioni di Assicurazione e la normativa vigente.
        </p>
        <LegalFooterLinks className="pb-6" />
      </div>
    </div>
  );
}
